import React, { useState, useEffect } from 'react';
import { 
    MessageCircle, 
    Trash2, 
    Search,
    User,
    AlertCircle,
    CheckCircle,
    Clock,
    XCircle
} from 'lucide-react';
import { obtenerSugerencias, eliminarSugerencia, actualizarEstadoSugerencia } from '../../services/chatbotService';
import moment from 'moment';
import 'moment/locale/es';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
moment.locale('es');

const Sugerencias = () => {
    const [sugerencias, setSugerencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [sugerenciaToDelete, setSugerenciaToDelete] = useState(null);
    const [loadingStates, setLoadingStates] = useState({});

    useEffect(() => {
        cargarSugerencias();
    }, []);

    const cargarSugerencias = async () => {
        try {
            setLoading(true);
            const response = await obtenerSugerencias();
            if (response.success) {
                setSugerencias(response.data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
            }
        } catch (error) {
            console.error('Error al cargar sugerencias:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEliminarSugerencia = async (id) => {
        try {
            const response = await eliminarSugerencia(id);

            if (response.success) {
                setSugerencias(prevSugerencias => 
                    prevSugerencias.filter(sugerencia => sugerencia.id_sugerencia !== id)
                );
                setShowConfirmDialog(false);
                setSugerenciaToDelete(null);
                toast.success('Sugerencia eliminada exitosamente', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: { 
                        marginTop: '10px',
                        fontFamily: '"Exo 2", sans-serif',
                        fontWeight: 500
                    }
                });
            } else {
                toast.error('Error al eliminar la sugerencia', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: { 
                        marginTop: '10px',
                        fontFamily: '"Exo 2", sans-serif',
                        fontWeight: 500
                    }
                });
            }
        } catch (error) {
            console.error('Error al eliminar la sugerencia:', error);
            toast.error(`${error.message || 'Error al eliminar la sugerencia'}`, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: { 
                    marginTop: '10px',
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });
        }
    };

    const getEstadoIcon = (estado) => {
        switch (estado) {
            case 'implementada':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'revisada':
                return <Clock className="w-4 h-4 text-blue-500" />;
            case 'rechazada':
                return <XCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-yellow-500" />;
        }
    };

    const handleEstadoChange = async (id, nuevoEstado) => {
        try {
            setLoadingStates(prev => ({ ...prev, [id]: true }));
            const response = await actualizarEstadoSugerencia(id, nuevoEstado);
            
            if (response.success) {
                setSugerencias(prevSugerencias => 
                    prevSugerencias.map(sugerencia => 
                        sugerencia.id_sugerencia === id 
                            ? { ...sugerencia, estado: nuevoEstado }
                            : sugerencia
                    )
                );
                toast.success('Estado actualizado exitosamente', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } catch (error) {
            console.error('Error al actualizar estado:', error);
            toast.error('Error al actualizar el estado', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
        } finally {
            setLoadingStates(prev => ({ ...prev, [id]: false }));
        }
    };

    const sugerenciasFiltradas = sugerencias.filter(sugerencia => {
        const contenido = sugerencia?.contenido?.toLowerCase() || '';
        const rolUsuario = sugerencia?.rol_usuario?.toLowerCase() || '';
        const terminoBusqueda = busqueda.toLowerCase();
        
        return contenido.includes(terminoBusqueda) || 
               rolUsuario.includes(terminoBusqueda);
    });

    const ConfirmDialog = ({ onConfirm, onCancel }) => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
                <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="h-6 w-6 text-red-500" />
                    <h3 className="text-lg font-semibold text-gray-900">Confirmar eliminación</h3>
                </div>
                <p className="text-gray-600 mb-6">
                    ¿Estás seguro de que deseas eliminar esta sugerencia? Esta acción no se puede deshacer.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <ToastContainer />
            <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3 mb-6">
                    <MessageCircle className="h-6 w-6 text-purple-600" />
                    Sugerencias del Centro
                </h1>
                
                <div className="relative mb-6">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar por contenido"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl 
                                     focus:ring-2 focus:ring-purple-500 focus:border-transparent 
                                     transition-all duration-200 bg-white shadow-sm
                                     placeholder-gray-400 text-gray-600"
                        />
                        {busqueda && (
                            <button
                                onClick={() => setBusqueda('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )}
                    </div>
                    {busqueda && (
                        <div className="mt-2 text-sm text-gray-500">
                            {sugerenciasFiltradas.length === 0 ? (
                                <p>No se encontraron resultados para "{busqueda}"</p>
                            ) : (
                                <p>Se encontraron {sugerenciasFiltradas.length} resultado(s)</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
            ) : sugerenciasFiltradas.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-100">
                    <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No hay sugerencias disponibles</p>
                    <p className="text-gray-400 text-sm">Las sugerencias aparecerán aquí cuando los usuarios las envíen</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {sugerenciasFiltradas.map((sugerencia) => (
                        <div
                            key={sugerencia.id_sugerencia}
                            className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <p className="text-gray-900 text-lg mb-4">
                                        {sugerencia.contenido}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                <User className="h-4 w-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {sugerencia.rol_usuario}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {moment(sugerencia.fecha).format('DD/MM/YYYY')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <select
                                                    value={sugerencia.estado || 'pendiente'}
                                                    onChange={(e) => handleEstadoChange(sugerencia.id_sugerencia, e.target.value)}
                                                    disabled={loadingStates[sugerencia.id_sugerencia]}
                                                    className="appearance-none pl-8 pr-4 py-1 rounded-full text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                                                    style={{
                                                        backgroundColor: 
                                                            sugerencia.estado === 'implementada' ? '#f0fdf4' :
                                                            sugerencia.estado === 'revisada' ? '#eff6ff' :
                                                            sugerencia.estado === 'rechazada' ? '#fef2f2' :
                                                            '#fefce8',
                                                        borderColor:
                                                            sugerencia.estado === 'implementada' ? '#86efac' :
                                                            sugerencia.estado === 'revisada' ? '#93c5fd' :
                                                            sugerencia.estado === 'rechazada' ? '#fecaca' :
                                                            '#fef08a',
                                                        color:
                                                            sugerencia.estado === 'implementada' ? '#16a34a' :
                                                            sugerencia.estado === 'revisada' ? '#2563eb' :
                                                            sugerencia.estado === 'rechazada' ? '#dc2626' :
                                                            '#ca8a04'
                                                    }}
                                                >
                                                    <option value="pendiente">Pendiente</option>
                                                    <option value="revisada">Revisada</option>
                                                    <option value="implementada">Implementada</option>
                                                    <option value="rechazada">Rechazada</option>
                                                </select>
                                                <div className="absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                    {loadingStates[sugerencia.id_sugerencia] ? (
                                                        <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                                                    ) : (
                                                        getEstadoIcon(sugerencia.estado)
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setSugerenciaToDelete(sugerencia.id_sugerencia);
                                                    setShowConfirmDialog(true);
                                                }}
                                                className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-red-50"
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    onConfirm={() => handleEliminarSugerencia(sugerenciaToDelete)}
                    onCancel={() => {
                        setShowConfirmDialog(false);
                        setSugerenciaToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default Sugerencias;
